import { toast } from 'sonner'
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/lib/api'
import { getConsent, setConsent, type ConsentState } from '@/components/ConsentBanner'
import { validatePassword } from '@/components/auth/PasswordStrength'
import { QRCodeSVG } from 'qrcode.react'
import { Exports } from '@/pages/Exports'
import {
  User,
  Shield,
  Trash2,
  Loader2,
  AlertTriangle,
  Bell,
  Monitor,
  RotateCcw,
  Download,
  Smartphone,
  Copy,
  FileDown,
  Cookie,
} from 'lucide-react'

interface NotificationPrefs {
  renewal_reminder: boolean
  payment_failures: boolean
  security_alerts: boolean
  rate_limit_warnings: boolean
}

interface Session {
  id: string
  ip_address: string
  user_agent: string
  created_at: string
  last_used_at: string
}

export function Settings() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('tab') || 'profile'
  const isClosing = user?.status === 'closing'

  // Profile
  const [profileForm, setProfileForm] = useState({
    display_name: user?.display_name || '',
    timezone: user?.timezone || 'UTC',
  })
  const [saving, setSaving] = useState(false)

  // CHECKPOINT: PRD-10 FR-10.11.1 — Use browser-provided timezone list instead of hardcoded options.
  const timezones = useMemo(() => {
    try {
      return (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') || ['UTC']
    } catch {
      return ['UTC']
    }
  }, [])

  // Password
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' })

  // Two-factor authentication
  const [totpEnabled, setTotpEnabled] = useState(false)
  const [totpLoading, setTotpLoading] = useState(true)
  const [totpSetup, setTotpSetup] = useState<{ secret: string; provisioning_uri: string } | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [totpDisablePassword, setTotpDisablePassword] = useState('')
  const [enablingTotp, setEnablingTotp] = useState(false)
  const [disablingTotp, setDisablingTotp] = useState(false)

  const [prefs, setPrefs] = useState<NotificationPrefs>({
    renewal_reminder: true,
    payment_failures: true,
    security_alerts: true,
    rate_limit_warnings: true,
  })
  const [prefsLoading, setPrefsLoading] = useState(true)
  const [savingPrefs, setSavingPrefs] = useState(false)

  // Active sessions
  const [sessions, setSessions] = useState<Session[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [revokingSession, setRevokingSession] = useState<string | null>(null)

  // Analytics consent
  const [analyticsConsent, setAnalyticsConsent] = useState<ConsentState>('pending')

  // New account review (PRD-09)
  const [review, setReview] = useState<any>(null)
  const [reviewLoading, setReviewLoading] = useState(false)

  // Close account
  const [closing, setClosing] = useState(false)
  const [showCloseConfirm, setShowCloseConfirm] = useState(false)
  const [closeResult, setCloseResult] = useState<{
    grace_period_end?: string
    refund_eligible?: boolean
    refund_amount_cents?: number
    refund_status?: 'pending' | 'review' | 'succeeded' | 'failed' | null
  } | null>(null)
  const [restoring, setRestoring] = useState(false)


  const loadSessions = () => {
    setSessionsLoading(true)
    api
      .get('/account/sessions')
      .then((res) => setSessions(res.data.sessions || []))
      .catch(() => toast.error('Failed to load active sessions'))
      .finally(() => setSessionsLoading(false))
  }

  useEffect(() => {
    setAnalyticsConsent(getConsent())

    // CHECKPOINT: PRD-10 FR-10.11.3 — Load notification preferences on mount.
    api
      .get('/account/notification-preferences')
      .then((res) => setPrefs(res.data))
      .catch(() => toast.error('Failed to load notification preferences'))
      .finally(() => setPrefsLoading(false))

    // CHECKPOINT: PRD-10 FR-10.11.2 — Load active sessions on mount.
    loadSessions()

    // CHECKPOINT: PRD-10 FR-10.11.2 — Load 2FA status on mount.
    api
      .get('/account/totp/status')
      .then((res) => setTotpEnabled(res.data.enabled))
      .catch(() => toast.error('Failed to load 2FA status'))
      .finally(() => setTotpLoading(false))

    // CHECKPOINT: PRD-09 FR-9.2 — Load new-account review status for the authenticated account.
    setReviewLoading(true)
    api
      .get('/account/review')
      .then((res) => setReview(res.data))
      .catch(() => setReview(null))
      .finally(() => setReviewLoading(false))
  }, [])

  const saveProfile = async () => {
    setSaving(true)
    try {
      await api.put('/account/me', profileForm)
      toast.success('Profile updated')
    } catch {
      toast.error('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  // CHECKPOINT: PRD-01 FR-1.1.3 — Client-side password complexity validation before change.
  const changePassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('Passwords do not match')
      return
    }
    const validation = validatePassword(passwordForm.new)
    if (!validation.minLength) {
      toast.error('Password must be at least 8 characters long')
      return
    }
    if (!validation.upper) {
      toast.error('Password must contain at least one uppercase letter')
      return
    }
    if (!validation.lower) {
      toast.error('Password must contain at least one lowercase letter')
      return
    }
    if (!validation.digit) {
      toast.error('Password must contain at least one number')
      return
    }
    if (!validation.special) {
      toast.error('Password must contain at least one special character')
      return
    }
    try {
      await api.post('/account/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.new,
      })
      toast.success('Password changed')
      setPasswordForm({ current: '', new: '', confirm: '' })
    } catch {
      toast.error('Failed to change password')
    }
  }

  // CHECKPOINT: PRD-10 FR-10.11.2 — TOTP 2FA setup/enable/disable.
  const setupTotp = async () => {
    try {
      const res = await api.post('/account/totp/setup')
      setTotpSetup(res.data)
    } catch {
      toast.error('Failed to start 2FA setup')
    }
  }

  const enableTotp = async () => {
    if (totpCode.length !== 6) {
      toast.error('Enter the 6-digit code from your authenticator app')
      return
    }
    setEnablingTotp(true)
    try {
      await api.post('/account/totp/enable', { code: totpCode })
      setTotpEnabled(true)
      setTotpSetup(null)
      setTotpCode('')
      toast.success('Two-factor authentication enabled')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to enable 2FA')
    } finally {
      setEnablingTotp(false)
    }
  }

  const disableTotp = async () => {
    if (!totpDisablePassword) {
      toast.error('Enter your password to disable 2FA')
      return
    }
    setDisablingTotp(true)
    try {
      await api.post('/account/totp/disable', { password: totpDisablePassword })
      setTotpEnabled(false)
      setTotpDisablePassword('')
      toast.success('Two-factor authentication disabled')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to disable 2FA')
    } finally {
      setDisablingTotp(false)
    }
  }

  // CHECKPOINT: PRD-10 FR-10.11.3 — Save notification preferences.
  const savePrefs = async () => {
    setSavingPrefs(true)
    try {
      await api.put('/account/notification-preferences', prefs)
      toast.success('Notification preferences saved')
    } catch {
      toast.error('Failed to save notification preferences')
    } finally {
      setSavingPrefs(false)
    }
  }

  // CHECKPOINT: PRD-10 FR-10.11.2 — Revoke a single active session.
  const revokeSession = async (sessionId: string) => {
    setRevokingSession(sessionId)
    try {
      await api.post(`/account/sessions/${sessionId}/revoke`)
      toast.success('Session revoked')
      loadSessions()
    } catch {
      toast.error('Failed to revoke session')
    } finally {
      setRevokingSession(null)
    }
  }

  // CHECKPOINT: PRD-01 FR-1.4.9 / FR-1.7.x — Close account with refund eligibility and grace period.
  const closeAccount = async () => {
    setClosing(true)
    try {
      const res = await api.post('/account/close', { reason: 'customer_request', confirm_delete: true })
      setCloseResult(res.data)
      toast.success(
        `Account closure scheduled. Grace period ends ${new Date(res.data.grace_period_end).toLocaleDateString()}.`,
        { duration: 6000 }
      )
      // CHECKPOINT: PRD-01 FR-1.7.4 — Refresh into the closure flow so the
      // account-closure guard cannot leave the user on stale Settings state.
      window.location.assign('/account-closure')
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to close account')
    } finally {
      setClosing(false)
      setShowCloseConfirm(false)
    }
  }

  // CHECKPOINT: PRD-01 FR-1.7.4 — Restore a closing account before purge.
  const restoreAccount = async () => {
    setRestoring(true)
    try {
      const res = await api.post('/account/restore')
      setCloseResult(null)
      toast.success(res.data.message || 'Account restored')
      if (res.data.requires_checkout) {
        window.location.href = '/billing'
        return
      }
      // Refresh user state so the closing banner disappears.
      window.location.reload()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to restore account')
    } finally {
      setRestoring(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account preferences</p>
      </div>

      {isClosing && (
        <div className="rounded-md bg-destructive/10 p-4 text-destructive text-sm flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">Your account is scheduled for closure.</p>
            <p className="mt-1">
              You can still log in and restore your account until the grace period ends.
            </p>
          </div>
        </div>
      )}

      {/* CHECKPOINT: PRD-10 FR-10.11.1 — Settings tabs for Profile, Security, Notifications, Export Data, Close Account. */}
      <Tabs defaultValue={initialTab} className="max-w-2xl">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="profile" className="gap-1.5">
            <User className="h-3.5 w-3.5 hidden sm:inline" />
            <span className="hidden sm:inline">Profile</span>
            <span className="sm:hidden">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-1.5">
            <Shield className="h-3.5 w-3.5 hidden sm:inline" />
            Security
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-1.5">
            <Bell className="h-3.5 w-3.5 hidden sm:inline" />
            <span className="hidden sm:inline">Notifications</span>
            <span className="sm:hidden">Alerts</span>
          </TabsTrigger>
          <TabsTrigger value="privacy" className="gap-1.5">
            <Cookie className="h-3.5 w-3.5 hidden sm:inline" />
            <span className="hidden sm:inline">Privacy</span>
            <span className="sm:hidden">Privacy</span>
          </TabsTrigger>
          <TabsTrigger value="export" className="gap-1.5">
            <FileDown className="h-3.5 w-3.5 hidden sm:inline" />
            <span className="hidden sm:inline">Export Data</span>
            <span className="sm:hidden">Export</span>
          </TabsTrigger>
          <TabsTrigger value="close" className="gap-1.5">
            <Trash2 className="h-3.5 w-3.5 hidden sm:inline" />
            Close
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium">Display Name</label>
                <Input
                  value={profileForm.display_name}
                  onChange={(e) => setProfileForm({ ...profileForm, display_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input value={user?.email || ''} disabled />
              </div>
              <div>
                <label className="text-sm font-medium">Timezone</label>
                <select
                  className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={profileForm.timezone}
                  onChange={(e) => setProfileForm({ ...profileForm, timezone: e.target.value })}
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
              <Button onClick={saveProfile} disabled={saving} size="sm">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Profile
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Current Password</label>
                  <Input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium">New Password</label>
                  <Input type="password" value={passwordForm.new} onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium">Confirm New Password</label>
                  <Input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })} />
                </div>
                <Button onClick={changePassword} size="sm" variant="secondary">
                  Change Password
                </Button>
              </div>

              <hr className="border-border" />

              {/* CHECKPOINT: PRD-10 FR-10.11.2 — 2FA toggle */}
              <div className="space-y-3">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-muted-foreground" />
                  Two-factor authentication
                </p>
                {totpLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : totpEnabled ? (
                  <div className="space-y-3">
                    <p className="text-xs text-green-600 font-medium">Two-factor authentication is enabled.</p>
                    <div>
                      <label className="text-sm font-medium">Current Password</label>
                      <Input
                        type="password"
                        placeholder="Enter your password to disable 2FA"
                        value={totpDisablePassword}
                        onChange={(e) => setTotpDisablePassword(e.target.value)}
                      />
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={disableTotp}
                      disabled={disablingTotp}
                    >
                      {disablingTotp && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Disable 2FA
                    </Button>
                  </div>
                ) : totpSetup ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Scan the QR code with your authenticator app, then enter the 6-digit code to confirm.
                    </p>
                    <div className="inline-block rounded-md bg-white p-2">
                      <QRCodeSVG value={totpSetup.provisioning_uri} size={160} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Input readOnly value={totpSetup.secret} className="font-mono text-xs" />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          navigator.clipboard.writeText(totpSetup.secret)
                          toast.success('Secret copied to clipboard')
                        }}
                        aria-label="Copy secret"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="6-digit code"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={enableTotp} disabled={enablingTotp}>
                        {enablingTotp && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Enable 2FA
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setTotpSetup(null); setTotpCode('') }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" onClick={setupTotp}>
                    Set up two-factor authentication
                  </Button>
                )}
              </div>

              <hr className="border-border" />

              {/* CHECKPOINT: PRD-09 FR-9.2 — Customer-visible new-account review status */}
              <div className="space-y-3">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  Account review
                </p>
                {reviewLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : review ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-md border border-border p-2.5">
                        <p className="text-xs text-muted-foreground">Pattern score</p>
                        <p className={`text-lg font-bold ${review.pattern_score >= 90 ? 'text-red-500' : review.pattern_score >= 70 ? 'text-amber-500' : 'text-emerald-500'}`}>
                          {review.pattern_score} / 100
                        </p>
                      </div>
                      <div className="rounded-md border border-border p-2.5">
                        <p className="text-xs text-muted-foreground">Status</p>
                        <p className="text-sm font-semibold capitalize">{review.status}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Emails reviewed: {review.emails_reviewed} / {review.max_emails_to_review}
                    </p>
                    {(review.status === 'throttled' || review.status === 'suspended') && (
                      <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-3 text-amber-400 text-xs flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <p>
                          Your account has been auto-{review.status} due to suspicious sending patterns.
                          Contact support if you believe this is an error.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No active account review.</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Monitor className="h-4 w-4 text-muted-foreground" />
                Active Sessions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {sessionsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No active sessions found.</p>
              ) : (
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between rounded-md border p-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {session.user_agent}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          IP: {session.ip_address} · Last used:{' '}
                          {new Date(session.last_used_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => revokeSession(session.id)}
                        disabled={revokingSession === session.id}
                      >
                        {revokingSession === session.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Revoke'
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Bell className="h-4 w-4 text-muted-foreground" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {prefsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Renewal reminders</p>
                      <p className="text-xs text-muted-foreground">7 days before renewal</p>
                    </div>
                    <Switch
                      checked={prefs.renewal_reminder}
                      onCheckedChange={(v) => setPrefs({ ...prefs, renewal_reminder: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Payment failures</p>
                      <p className="text-xs text-muted-foreground">Grace period alerts</p>
                    </div>
                    <Switch
                      checked={prefs.payment_failures}
                      onCheckedChange={(v) => setPrefs({ ...prefs, payment_failures: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Security alerts</p>
                      <p className="text-xs text-muted-foreground">Suspicious logins and changes</p>
                    </div>
                    <Switch
                      checked={prefs.security_alerts}
                      onCheckedChange={(v) => setPrefs({ ...prefs, security_alerts: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Rate limit warnings</p>
                      <p className="text-xs text-muted-foreground">When you approach API limits</p>
                    </div>
                    <Switch
                      checked={prefs.rate_limit_warnings}
                      onCheckedChange={(v) => setPrefs({ ...prefs, rate_limit_warnings: v })}
                    />
                  </div>
                  <Button onClick={savePrefs} disabled={savingPrefs} size="sm">
                    {savingPrefs && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Notifications
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="privacy" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Cookie className="h-4 w-4 text-muted-foreground" />
                Analytics & Cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Share usage analytics</p>
                  <p className="text-xs text-muted-foreground">
                    Helps us improve PurpleToad Mail. No third-party ad trackers.
                  </p>
                </div>
                <Switch
                  checked={analyticsConsent === 'granted'}
                  onCheckedChange={(v) => {
                    const state = v ? 'granted' : 'denied'
                    setConsent(state)
                    setAnalyticsConsent(state)
                    toast.success(v ? 'Analytics enabled' : 'Analytics disabled')
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                We use analytics cookies to understand product usage and fix issues. No ads, no third-party trackers.{' '}
                <a href="https://purpletoadmail.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Privacy Policy
                </a>
                .
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHECKPOINT: PRD-10 FR-10.15.1 — Export Data tab in Settings. */}
        <TabsContent value="export" className="mt-4 space-y-4">
          <Exports embedded />
        </TabsContent>

        <TabsContent value="close" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-destructive">
                <Trash2 className="h-4 w-4" />
                Close Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isClosing ? (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-destructive">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p className="text-sm">
                      Your account is scheduled for closure on{' '}
                      {closeResult?.grace_period_end
                        ? new Date(closeResult.grace_period_end).toLocaleDateString()
                        : 'the end of the grace period'}
                      . Restore it now to keep your service active.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={restoreAccount} disabled={restoring}>
                      {restoring && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Restore Account
                    </Button>
                  </div>
                </div>
              ) : !showCloseConfirm ? (
                <>
                  <p className="text-sm text-muted-foreground mb-3">
                    Closing your account enters a 30-day grace period. Your data will be permanently deleted after.
                  </p>
                  <p className="text-sm text-muted-foreground mb-3">
                    If you are within 30 days of your first payment, you may be eligible for a full refund.
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => window.location.href = '/settings?tab=export'}>
                      <Download className="mr-2 h-4 w-4" />
                      Export Data First
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => setShowCloseConfirm(true)}>
                      Close Account
                    </Button>
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3">
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">
                      Are you sure? This will schedule your account for closure. You have 30 days to restore it from this page.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="destructive" size="sm" onClick={closeAccount} disabled={closing}>
                      {closing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Yes, Close My Account
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowCloseConfirm(false)} disabled={closing}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
